import { amountToCents, centsToAmount } from './amounts';
import type { Urgency } from './model';

/**
 * Wat de inspecteur op een antwoord overschrijft: rapporttekst, bedragen,
 * urgentie, kostengroep en NHG-classificatie (`inspection_answer`).
 *
 * Een eigen bedrag vervangt voor die urgentie het berekende bedrag van het hele
 * antwoord, en telt één keer: op de eerste gekozen optie van dat antwoord in
 * boomvolgorde. Dat telt door in de bevinding, de knooptotalen en het
 * dossiertotaal; de engine rekent het, in de app en in de API precies gelijk
 * (`graph-semantics.md` §10.2). Een bedrag dat `null` is, laat het berekende
 * bedrag staan; `"0.00"` zet het op nul. Tekst, urgentie en NHG gelden voor
 * diezelfde eerste bevinding.
 */
export interface AnswerOverrides {
    reportText: string | null;
    /** Waar het gebrek zit, in een handvol woorden; `null` als de inspecteur niets zei. */
    location: string | null;
    directCost: string | null;
    termCost: string | null;
    improvementCost: string | null;
    costGroupId: number | null;
    urgency: string | null;
    nhgClassification: string | null;
}

export type CostOverrideFields = Pick<
    AnswerOverrides,
    'directCost' | 'termCost' | 'improvementCost'
>;

/** Minstens één eigen bedrag; een leeg of onleesbaar bedrag telt, net als in de engine, niet. */
export function hasCostOverride(overrides: CostOverrideFields): boolean {
    return (
        amountToCents(overrides.directCost) !== null ||
        amountToCents(overrides.termCost) !== null ||
        amountToCents(overrides.improvementCost) !== null
    );
}

export function hasOverrides(overrides: AnswerOverrides): boolean {
    return (
        hasCostOverride(overrides) ||
        (overrides.reportText !== null && overrides.reportText.trim() !== '') ||
        (overrides.location !== null && overrides.location.trim() !== '') ||
        overrides.urgency !== null ||
        overrides.nhgClassification !== null
    );
}

/**
 * "Herstelkosten" bij een urgentie: het bedrag komt in die kolom en de andere
 * twee worden nul, zodat het bedrag het hele berekende bedrag vervangt in plaats
 * van erbij op te tellen. Een leeg veld (`null`) zet alle drie terug op het
 * berekende bedrag.
 */
export function repairCostFields(cents: number | null, urgency: Urgency): CostOverrideFields {
    if (cents === null) {
        return { directCost: null, termCost: null, improvementCost: null };
    }

    const amount = centsToAmount(cents);
    const zero = centsToAmount(0);

    return {
        directCost: urgency === 'direct' ? amount : zero,
        termCost: urgency === 'term' ? amount : zero,
        improvementCost: urgency === 'improvement' ? amount : zero,
    };
}
